import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Box, Button, Typography, Link, Grid, Autocomplete, TextField, } from "@mui/material";
import EmailComponent from "../../../../Components/Ui_elements/EmailComponent";
import TextFieldComponent from "../../../../Components/TextFieldComponent";
import { useDispatch, useSelector } from "react-redux";
import { usersList } from "../../../../redux/Thunk/InvoiceModule/InvoiceThunk";
import { downloadPdfFromBase64 } from "../../../../Helper/DownloadPdfFromBase";
import { formatDateOnly } from "../../../../Helper/generalhelper";

const EmailClient = forwardRef(({ general = false, showFromProfile = false, invoiceNumber, dueDate, email, invoiceId, pdfData, outstanding_remainder, credit_notes_email }, ref) => {

    const dispatch = useDispatch();
    const { users_list, email_template } = useSelector((state) => state?.root?.InvoiceModule);
    const [errors, setErrors] = useState({});
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);
    const [formData, setFormData] = useState({
        subject: "",
        to: email || "",
        cc: [],
        bcc: [],
        attachment: null,
        defaultAttachment: null,
        template_content: "",
    });

    useEffect(() => {
        if(!users_list?.data){
            dispatch(usersList());
        }
    }, []);

    const normalizeEmailHtml = (html) => {
        const safe = html === null || html === undefined ? "" : String(html);
        // Strip stray meta/title-only paragraphs that can appear in stored templates
        return safe
            .replace(/^<p[^>]*>\s*((<meta[^>]*>\s*)*(<title[^>]*>.*?<\/title>\s*)*)(<br\s*\/?>)?\s*<\/p>/i, "")
            .replace(/<p[^>]*>\s*((<meta[^>]*>\s*)+|(<title[^>]*>.*?<\/title>\s*))(?:<br\s*\/?>)?\s*<\/p>/gi, "");
    };

    const buildEmailBodyHtml = ({ templateHtml, signatureHtml, spacerHeight = 20 }) => {
        const cleanedTemplate = normalizeEmailHtml(templateHtml);
        const cleanedSignature = normalizeEmailHtml(signatureHtml);
        const spacer = `<div style="height:${spacerHeight}px;"></div>`;
        return `<div class="tms-email-body">${cleanedTemplate}${spacer}${cleanedSignature}</div>`;
    };

    // Handle initial PDF data and template setup
    useEffect(() => {
        if (pdfData?.file) {
            setFormData(prev => ({
                ...prev,
                defaultAttachment: pdfData.file
            }));
        }
    }, [pdfData]);

    // Handle email template updates
    useEffect(() => {
        const safeString = (val) => (val === null || val === undefined ? "" : String(val));
        const safeSubjectPrefix = safeString(email_template?.template?.template_subject).trim();
        const safeInvoiceNo = safeString(invoiceNumber).trim();
        const safeDueDateRaw = safeString(dueDate).trim();
        const safeDueDate = formatDateOnly(safeDueDateRaw) || safeDueDateRaw;
        const safeEmail = safeString(email).trim();
        const safeSignature = safeString(email_template?.signature);
        const subject = general
            ? safeInvoiceNo
            : `${safeSubjectPrefix} ${safeInvoiceNo}`.trim();

        if (email_template) {
            if (general) {
                // Only use the signature when general is true
                setFormData(prev => ({
                    ...prev,
                    subject: subject,
                    to: safeEmail,
                    template_content: buildEmailBodyHtml({
                        templateHtml: "",
                        signatureHtml: safeSignature,
                        spacerHeight: 100
                    })
                }));
                if (showFromProfile) {
                    setFormData(prev => ({
                        ...prev,
                        subject: subject,
                        to: safeEmail,
                        template_content: ""
                    }));
                }
            } else {
                // Use the full template content when general is false
                const templateContentRaw = safeString(email_template?.template?.template_content);
                const content = templateContentRaw
                    .replace('[INVOICENO]', safeInvoiceNo)
                    .replace('[PAYDUE]', safeDueDate);

                setFormData(prev => ({
                    ...prev,
                    subject: subject,
                    to: safeEmail,
                    template_content: buildEmailBodyHtml({
                        templateHtml: content,
                        signatureHtml: safeSignature,
                        spacerHeight: 20
                    })
                }));
            }
        }
    }, [email_template, invoiceNumber, dueDate, email, general, showFromProfile]);

    const readFileAsBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        // Subject validation
        if (!formData.subject.trim()) {
            newErrors.subject = true;
            isValid = false;
        }

        // Email validation
        if (!formData.to.trim()) {
            newErrors.to = true;
            isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.to.trim())) {
            newErrors.to = true;
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return null;
        }

        let attachmentBase64 = null;

        if (formData.attachment) {
            attachmentBase64 = await readFileAsBase64(formData.attachment);
        } else if (formData.defaultAttachment) {
            attachmentBase64 = formData.defaultAttachment;
        }

        const ccEmails = formData.cc.map(item => item.label).join(',');
        const bccEmails = formData.bcc.map(item => item.label).join(',');

        let payload = {};
        if (general) {
            payload = {
                file: attachmentBase64 || null,
                data: {
                    vEmailAddress: formData.to,
                    msgEmailSubject: formData.subject,
                    messageData: formData.template_content,
                    cc: ccEmails,
                    bcc: bccEmails
                }
            }
        } else {
            payload = {
                pdfData: attachmentBase64,
                invoice_id: invoiceId,
                invoiceno: invoiceNumber || '',
                invoiceDue: dueDate || '',
                companycontactEmail: formData.to,
                outstanding_reminder: outstanding_remainder,
                credit_notes_email: credit_notes_email,
                data: {
                    messageData: formData.template_content,
                    msgEmailSubject: formData.subject,
                    vEmailAddress: formData.to,
                    cc: ccEmails,
                    bcc: bccEmails,
                    file: attachmentBase64 || null
                }
            };
        }
        return payload;
    };

    useImperativeHandle(ref, () => ({
        handleSubmit,
    }));

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFormData(prev => ({ ...prev, attachment: file }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const userOptions = users_list?.data?.map((user) => ({
        value: user.iUserId,
        label: user.vEmailAddress,
    }));

    return (
        <Box
            sx={{
                p: 4,
                bgcolor: "white",
                borderRadius: 2,
                boxShadow: 1,
                display: "flex",
                justifyContent: "center",
            }}
        >
            <Box sx={{ width: "100%", maxWidth: 700 }}>
                <Grid container spacing={2} direction="column">
                    {/* Subject */}
                    <Grid item>
                        <Box display="flex" alignItems="center">
                            <Typography sx={{ width: 120 }}>Subject:</Typography>
                            <TextFieldComponent
                                name="subject"
                                type={"text"}
                                value={formData.subject}
                                onChange={handleChange}
                                isNotMandatory
                                fullWidth
                                error={errors.subject}
                            />
                        </Box>
                    </Grid>

                    {/* To Email with CC/BCC toggles */}
                    <Grid item>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
                                <Typography sx={{ width: 120 }}>Email Id:</Typography>
                                <TextFieldComponent
                                    name="to"
                                    type={"email"}
                                    value={formData.to}
                                    onChange={handleChange}
                                    isNotMandatory
                                    fullWidth
                                    error={errors.to}
                                />
                            </Box>
                            <Box>
                                <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{ ml: 2, cursor: "pointer", color: "primary.main" }}
                                    onClick={() => setShowCc(!showCc)}
                                >
                                    Cc
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{ ml: 2, cursor: "pointer", color: "primary.main" }}
                                    onClick={() => setShowBcc(!showBcc)}
                                >
                                    Bcc
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    {/* CC */}
                    {showCc && (
                        <Grid item>
                            <Box display="flex" alignItems="center">
                                <Typography sx={{ width: 120 }}>Cc:</Typography>
                                <Autocomplete
                                    multiple
                                    fullWidth
                                    size="small"
                                    disableCloseOnSelect
                                    options={userOptions || []}
                                    getOptionLabel={(option) => option.label}
                                    value={formData.cc}
                                    onChange={(event, newValue) =>
                                        setFormData(prev => ({ ...prev, cc: newValue }))
                                    }
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    renderInput={(params) => (
                                        <TextField {...params} variant="outlined" placeholder="" />
                                    )}
                                />
                            </Box>
                        </Grid>
                    )}

                    {/* BCC */}
                    {showBcc && (
                        <Grid item>
                            <Box display="flex" alignItems="center">
                                <Typography sx={{ width: 120 }}>Bcc:</Typography>
                                <Autocomplete
                                    multiple
                                    fullWidth
                                    size="small"
                                    disableCloseOnSelect
                                    options={userOptions || []}
                                    getOptionLabel={(option) => option.label}
                                    value={formData.bcc}
                                    onChange={(event, newValue) =>
                                        setFormData(prev => ({ ...prev, bcc: newValue }))
                                    }
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    renderInput={(params) => (
                                        <TextField {...params} variant="outlined" placeholder="" />
                                    )}
                                />
                            </Box>
                        </Grid>
                    )}

                    {/* Attachments */}
                    <Grid item>
                        <Box display="flex" alignItems="center">
                            <Typography sx={{ width: 120 }}>Attachments:</Typography>
                            {formData.attachment ? (
                                <>
                                    <Typography variant="body2">
                                        {formData.attachment.name}
                                    </Typography>

                                </>
                            ) : formData.defaultAttachment ? (
                                <>
                                    <Button size="small" onClick={() => downloadPdfFromBase64(formData.defaultAttachment, pdfData?.name)} sx={{ ml: 1 }}>
                                        {pdfData?.name || 'Invoice.pdf'}
                                    </Button>
                                </>
                            ) : (
                                <Typography>No file chosen</Typography>
                            )}
                            <Button
                                component="label"
                                variant="outlined"
                                size="small"
                                sx={{ ml: 2 }}
                            >
                                Choose File
                                <input type="file" hidden onChange={handleFileChange} />
                            </Button>
                        </Box>
                    </Grid>

                    {/* Email Body */}
                    <Grid item>
                        <EmailComponent formData={formData} setFormData={setFormData} />
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
});

export default EmailClient;